import { useEffect, useState } from 'react'
import { authFetch } from '../api/authFetch'
import InlineAlert from '../components/InlineAlert'
import SortedTableHeader from '../components/SortedTableHeader'
import { FormattedDate } from '../components/FormattedDate'

const COLUMNS = [
    { field: 'report_date', label: 'Date' },
    { field: 'new_chats_count', label: 'New users' },
    { field: 'new_jobs_count', label: 'New jobs' },
    { field: 'new_slots_count', label: 'New slots' },
    { field: 'new_notifications_count', label: 'New notifications' },
    { field: 'computed_at', label: 'Computed at' }
]

export default function DailyReports() {
    const [reports, setReports] = useState([])
    const [loading, setLoading] = useState(true)
    const [loadError, setLoadError] = useState(null)
    const [sort, setSort] = useState({ field: 'report_date', direction: 'DESC' })

    useEffect(() => {
        fetchReports()
    }, [sort])

    const fetchReports = async () => {
        setLoading(true)
        setLoadError(null)
        try {
            const params = new URLSearchParams({
                sortField: sort.field,
                sortDirection: sort.direction
            })
            const res = await authFetch(`/api/daily-reports?${params}`)
            const data = await res.json()
            if (!res.ok) {
                throw new Error(data.error || 'Failed to load daily reports')
            }
            setReports(data.reports || [])
        } catch (err) {
            console.error('Failed to fetch daily reports:', err)
            setLoadError(err.message || 'Failed to load daily reports')
        } finally {
            setLoading(false)
        }
    }

    const handleSort = (field) => {
        setSort((prev) => {
            if (prev.field === field) {
                return { field, direction: prev.direction === 'ASC' ? 'DESC' : 'ASC' }
            }
            return { field, direction: 'DESC' }
        })
    }

    return (
        <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-6">Daily reports</h1>

            {loadError && (
                <InlineAlert variant="error" onDismiss={() => setLoadError(null)}>
                    <p>{loadError}</p>
                </InlineAlert>
            )}

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                {loading ? (
                    <div className="flex items-center justify-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                    {COLUMNS.map((col) => (
                                        <SortedTableHeader
                                            key={col.field}
                                            field={col.field}
                                            label={col.label}
                                            sort={sort}
                                            onSort={handleSort}
                                        />
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {reports.map((row) => (
                                    <tr key={row.report_date} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 text-sm font-medium text-gray-900 whitespace-nowrap">
                                            {row.report_date}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-900 tabular-nums">
                                            {row.new_chats_count}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-900 tabular-nums">
                                            {row.new_jobs_count}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-900 tabular-nums">
                                            {row.new_slots_count}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-900 tabular-nums">
                                            {row.new_notifications_count}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-600 whitespace-nowrap">
                                            <FormattedDate isoDate={row.computed_at} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {reports.length === 0 && (
                            <p className="px-6 py-12 text-center text-gray-500 text-sm">
                                No daily reports yet. They appear after the cron job runs.
                            </p>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}
